import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getAppMode } from '../appMode';
import { isSaasMarketingContentPath } from '../components/SaasMarketingBootShell';
import {
  applyThemeToDocument,
  applyDayThemeLock,
  effectiveDocumentTheme,
  MARKETING_THEME_KEY,
  oppositeResolvedTheme,
  readStoredThemeMode,
  sanitizeMarketingThemeMode,
  sanitizeThemeMode,
  shouldForceDayTheme,
  storeThemeMode,
  systemPrefersDark,
  type MarketingThemeMode,
  type ResolvedTheme,
  type ThemeMode,
} from '../design/theme';
import {
  DEFAULT_LOADER_APPEARANCE,
  DEFAULT_NIGHT_PALETTE_ID,
  LOADER_APPEARANCE_KEY,
  NIGHT_PALETTE_KEY,
  resolveNightPalette,
  sanitizeLoaderAppearance,
  sanitizeNightPaletteId,
  type LoaderAppearance,
  type NightPaletteId,
} from '../design/nightPalette';
import { parseAppConfigBoolean, parseAppConfigString } from '../lib/billingStatusLogic';

interface ThemeContextValue {
  /** What the user picked: light, dark, or follow the device. */
  mode: ThemeMode;
  /** What is actually rendered right now. */
  theme: ResolvedTheme;
  /** False when the platform admin has switched dark mode off. */
  darkModeAllowed: boolean;
  /** Super Admin: bright loader vs follow workspace night. */
  loaderAppearance: LoaderAppearance;
  setMode: (mode: ThemeMode) => void;
  cycleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'light',
  theme: 'light',
  darkModeAllowed: false,
  loaderAppearance: DEFAULT_LOADER_APPEARANCE,
  setMode: () => undefined,
  cycleMode: () => undefined,
});

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

/**
 * Owns the applied theme. The preference is per user and stored locally, so it
 * follows a person across their own tabs without a round trip, while the
 * platform switch (`enable_dark_mode`) can override everyone at once.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isMarketingContent = isSaasMarketingContentPath(pathname);
  const forceDay = shouldForceDayTheme(
    typeof window !== 'undefined' ? window.location.hostname : '',
    getAppMode(),
  );
  const [userId, setUserId] = useState<string | null>(null);
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredThemeMode() ?? 'light');
  const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark());
  const [darkModeAllowed, setDarkModeAllowed] = useState(false);
  const [marketingTheme, setMarketingTheme] = useState<MarketingThemeMode>('light');
  const [nightPaletteId, setNightPaletteId] = useState<NightPaletteId>(DEFAULT_NIGHT_PALETTE_ID);
  const [loaderAppearance, setLoaderAppearance] = useState<LoaderAppearance>(DEFAULT_LOADER_APPEARANCE);
  // Read inside the config callback, which can resolve before or after auth.
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getUser().then(({ data }) => {
      if (cancelled) return;
      const id = data.user?.id ?? null;
      userIdRef.current = id;
      setUserId(id);
      // A per-user preference overrides the anonymous one saved pre-login.
      const stored = readStoredThemeMode(id);
      if (stored) setModeState(stored);
    });

    supabase
      .from('app_config')
      .select('key, value')
      .in('key', [
        'enable_dark_mode',
        'theme_mode',
        MARKETING_THEME_KEY,
        NIGHT_PALETTE_KEY,
        LOADER_APPEARANCE_KEY,
      ])
      .then(({ data }) => {
        if (cancelled) return;

        for (const row of data ?? []) {
          if (row.key === 'enable_dark_mode') {
            setDarkModeAllowed(!forceDay && parseAppConfigBoolean(row.value ?? false));
          }
          // The platform default only applies to people who have not chosen.
          if (row.key === 'theme_mode' && !readStoredThemeMode(userIdRef.current)) {
            setModeState(sanitizeThemeMode(row.value));
          }
          if (row.key === MARKETING_THEME_KEY) {
            setMarketingTheme(sanitizeMarketingThemeMode(parseAppConfigString(row.value, 'light')));
          }
          if (row.key === NIGHT_PALETTE_KEY) {
            setNightPaletteId(
              sanitizeNightPaletteId(parseAppConfigString(row.value, DEFAULT_NIGHT_PALETTE_ID)),
            );
          }
          if (row.key === LOADER_APPEARANCE_KEY) {
            setLoaderAppearance(
              sanitizeLoaderAppearance(parseAppConfigString(row.value, DEFAULT_LOADER_APPEARANCE)),
            );
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const theme = effectiveDocumentTheme(mode, prefersDark, darkModeAllowed, {
    isMarketingContent,
    marketingTheme,
    forceDay,
  });
  const nightTokens = useMemo(() => resolveNightPalette(nightPaletteId), [nightPaletteId]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (forceDay) {
      applyDayThemeLock(document.documentElement);
      return;
    }
    applyThemeToDocument(document.documentElement, theme, nightTokens);
  }, [theme, nightTokens, forceDay]);

  const setMode = useCallback(
    (next: ThemeMode) => {
      if (forceDay) return;
      const sanitized = sanitizeThemeMode(next);
      setModeState(sanitized);
      storeThemeMode(sanitized, userId);
    },
    [userId, forceDay],
  );

  const allowed = forceDay ? false : darkModeAllowed;

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode: forceDay ? 'light' : mode,
      theme,
      darkModeAllowed: allowed,
      loaderAppearance,
      setMode,
      cycleMode: () => setMode(oppositeResolvedTheme(theme)),
    }),
    [mode, theme, allowed, loaderAppearance, setMode, forceDay],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
